import { useState } from 'react';

import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useParams } from 'react-router-dom';

import { Page } from '@/components/layout/Page';
import { CanvasRoutes, RouterConfig } from '@/configs/routes';
import { RunConfigDialog } from '@/features/decisions/components/backtests/RunConfigDialog';
import { StatusBadge } from '@/features/decisions/components/backtests/StatusBadge';
import { MAX_COMPARISON_CANDIDATES } from '@/features/decisions/hooks/useComparisonCandidates';
import { useExecutionConfig } from '@/features/decisions/hooks/useExecutionConfig';
import { useScenario } from '@/features/decisions/hooks/useScenario';
import { useDecisionsContext } from '@/features/decisions/providers/DecisionsProvider';
import type { ExecutionDetail, ExperimentLabel } from '@/features/decisions/scenarios.api.types';
import { simulationWindowOf } from '@/features/decisions/utils/simulationWindow';
import { buildSupersetRunDeepLink } from '@/features/decisions/utils/superset';

const COLUMNS = ['Run', 'Status', 'Submitted', 'Duration', 'Candidates', 'Config', 'Summary', 'Superset'] as const;

// Past this many, wrapping pills push the row across several lines and the table stops
// reading as a table. Rows at or under this render exactly as before.
const MAX_VISIBLE_CANDIDATES = 5;

function formatSubmitted(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

// Only meaningful for a run that actually finished successfully — `failed` may have a
// `started_at` with no `finished_at`, and neither is set until Execution Service reports it.
function formatDuration(run: ExecutionDetail): string {
  if (run.status !== 'succeeded' || !run.started_at || !run.finished_at) return '—';

  const start = new Date(run.started_at);
  const end = new Date(run.finished_at);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '—';

  const totalMinutes = Math.round((end.getTime() - start.getTime()) / 60_000);
  if (totalMinutes < 0) return '—';
  if (totalMinutes < 1) return '<1m';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours === 0 ? `${minutes}m` : `${hours}h ${minutes}m`;
}

// Enabled on `succeeded` only, assuming a successful run has been backfilled into ClickHouse.
// That is an assumption: the runner's backfill trigger never raises, so a run can succeed with
// nothing loaded, in which case the page renders empty rather than erroring.
//
// A link rather than a button, so it is right-clickable and copyable, and the query string makes
// the destination shareable.
//
// Context is seeded on click as well, because not every onward hop carries the search string:
// FcfContributorsTable links to Product / Stocking Locations with a plain Link that keeps only
// sku, site, scenario_run_id and run_id. Without the context copy, that page loses the experiment
// ids, falls back to DEFAULT_PERSONA's, and its data query 404s.
function SummaryLink({ run }: { run: ExecutionDetail }) {
  const { set } = useDecisionsContext();
  const succeeded = run.status === 'succeeded';
  // Only for a row that shows a link, and cached forever — a submitted config is immutable.
  const { data } = useExecutionConfig(run.scenario_run_id, run.run_id, succeeded);
  const window = simulationWindowOf(data?.config_body);

  if (!succeeded) return <PlaceholderCell label="View summary" />;

  // Position 1 is the reference the others are compared against — configs list the incumbent
  // first. `experiment_id` carries the comparisons only: the service adds the reference itself,
  // and including it there collapses the response to the reference bar alone.
  //
  // Both must be sent. Passing only the reference leaves the comparison on
  // DEFAULT_PERSONA.experimentIds, which belong to a different scenario and produce no bars.
  //
  // Capped at MAX_COMPARISON_CANDIDATES: Value Opportunity's picker enforces the same cap, so a
  // run with more comparisons than that would otherwise land there with every one of them
  // selected instead of a swappable subset.
  const reference = run.experiments.find((e) => e.position === 1)?.experiment_id;
  const comparisons = run.experiments
    .filter((e) => e.experiment_id !== reference)
    .map((e) => e.experiment_id)
    .slice(0, MAX_COMPARISON_CANDIDATES);

  const params = new URLSearchParams({ scenario_run_id: run.scenario_run_id, run_id: run.run_id });
  if (reference) params.set('ref_experiment_id', reference);
  if (comparisons.length) params.set('experiment_id', comparisons.join(','));
  // Without the run's own window the page keeps DEFAULT_PERSONA's, whose period_to is used as the
  // decision date and sits past SIM_END, so coverage and stockout return 404 and render empty.
  if (window) {
    params.set('period_from', window.periodFrom);
    params.set('period_to', window.periodTo);
  }

  const seedContext = () => {
    set('scenario_run_id', run.scenario_run_id);
    set('run_id', run.run_id);
    if (reference) set('ref_experiment_id', reference);
    if (window) {
      set('period_from', window.periodFrom);
      set('period_to', window.periodTo);
    }
  };

  return (
    <Link
      to={`${RouterConfig[CanvasRoutes.DecisionsValueOpportunity].path}?${params}`}
      onClick={seedContext}
      className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#274483] hover:text-[#4A6BAD]"
    >
      View summary
      <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
    </Link>
  );
}

// Same backfill assumption as SummaryLink: Superset's Backtest Performance dashboard reads
// sim.results out of ClickHouse, which only has this run's rows once it has succeeded.
// Opens in a new tab — this leaves the backtest results page, unlike View config/View summary.
function SupersetLink({ run }: { run: ExecutionDetail }) {
  const href =
    run.status === 'succeeded'
      ? buildSupersetRunDeepLink(
          run.scenario_run_id,
          run.run_id,
          run.experiments.map((e) => e.experiment_id),
        )
      : undefined;

  if (!href) return <PlaceholderCell label="Open in Superset" />;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#274483] hover:text-[#4A6BAD]"
    >
      Open in Superset
      <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
    </a>
  );
}

function CandidatePill({ experiment }: { experiment: ExperimentLabel }) {
  return (
    <span
      title={experiment.experiment_id}
      className="rounded bg-[#F1F2F5] px-2 py-0.5 text-[12px] font-medium text-[#5C5F72]"
    >
      {experiment.experiment_label ?? experiment.experiment_id}
    </span>
  );
}

function CandidatesDialog({
  experiments,
  open,
  onClose,
}: {
  experiments: ExperimentLabel[];
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-xl">
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <DialogTitle className="text-[16px] font-semibold text-[#1A1C2E]">
              Candidates ({experiments.length})
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-[#9295A5] hover:bg-[#F1F5F9] hover:text-[#1A1C2E]"
              aria-label="Close"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 overflow-auto p-5">
            {experiments.map((e) => (
              <CandidatePill key={e.experiment_id} experiment={e} />
            ))}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

function CandidatesCell({ experiments }: { experiments: ExperimentLabel[] }) {
  const [open, setOpen] = useState(false);

  if (experiments.length === 0) return <span className="text-[14px] text-[#B0B2BD]">—</span>;

  const visible = experiments.slice(0, MAX_VISIBLE_CANDIDATES);
  const hiddenCount = experiments.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((e) => (
        <CandidatePill key={e.experiment_id} experiment={e} />
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="cursor-pointer rounded bg-[#EEF1FB] px-2 py-0.5 text-[12px] font-medium text-[#274483] hover:bg-[#E1E6F7]"
        >
          +{hiddenCount} more
        </button>
      )}
      <CandidatesDialog experiments={experiments} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

// Shown when a run has not succeeded, so there is nothing loaded to summarise.
function PlaceholderCell({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#B0B2BD]">
      {label}
      <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
    </span>
  );
}

function StateRow({ children }: { children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={COLUMNS.length} className="px-6 py-10 text-center text-[14px] text-[#5C5F72]">
        {children}
      </td>
    </tr>
  );
}

function RunRow({ run }: { run: ExecutionDetail }) {
  const [configOpen, setConfigOpen] = useState(false);
  const duration = formatDuration(run);

  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-[#F5F6F8]/70">
      <td className="px-6 py-4">
        <code className="text-[12px] text-[#1A1C2E]">{run.run_id}</code>
        <span className="block text-[12px] text-[#9295A5]">by {run.submitted_by}</span>
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={run.status ?? null} />
      </td>
      <td className="px-6 py-4 text-[12px] whitespace-nowrap text-[#5C5F72]">{formatSubmitted(run.submitted_at)}</td>
      <td className="px-6 py-4 text-[14px] whitespace-nowrap">
        <span className={duration === '—' ? 'text-[#B0B2BD]' : 'text-[#5C5F72]'}>{duration}</span>
      </td>
      <td className="px-6 py-4">
        <CandidatesCell experiments={run.experiments} />
      </td>
      <td className="px-6 py-4">
        <button
          type="button"
          onClick={() => setConfigOpen(true)}
          className="inline-flex cursor-pointer items-center gap-1.5 text-[14px] font-medium text-[#274483] hover:text-[#4A6BAD]"
        >
          View config
          <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
        </button>
        <RunConfigDialog
          scenarioRunId={run.scenario_run_id}
          runId={run.run_id}
          open={configOpen}
          onClose={() => setConfigOpen(false)}
        />
      </td>
      <td className="px-6 py-4">
        <SummaryLink run={run} />
      </td>
      <td className="px-6 py-4">
        <SupersetLink run={run} />
      </td>
    </tr>
  );
}

export function ScenarioRuns() {
  const { scenarioRunId = '' } = useParams();
  const { data, isPending, isError, error } = useScenario(scenarioRunId);

  const runs = data?.executions ?? [];

  return (
    <Page title={data?.label ?? scenarioRunId}>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 px-6 pt-5 pb-3">
          <div>
            <h2 className="text-[16px] font-semibold text-[#1A1C2E]">Runs</h2>
            <code className="text-[12px] text-[#9295A5]">{scenarioRunId}</code>
          </div>
          {!isPending && !isError && (
            <span className="text-[13px] text-[#9295A5]">
              {runs.length} {runs.length === 1 ? 'run' : 'runs'}
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-y border-gray-200 text-left text-[13px] font-semibold text-[#1A1C2E]">
                {COLUMNS.map((label) => (
                  <th key={label} className="px-6 py-3 font-semibold">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isPending && <StateRow>Loading runs…</StateRow>}
              {isError && (
                <StateRow>
                  <span className="text-[#B4291F]">Could not load this backtest. {error.message}</span>
                </StateRow>
              )}
              {!isPending && !isError && runs.length === 0 && (
                <StateRow>No runs yet. Submit one from the Execute page.</StateRow>
              )}
              {!isPending && !isError && runs.map((run) => <RunRow key={run.run_id} run={run} />)}
            </tbody>
          </table>
        </div>
      </div>
    </Page>
  );
}
